import { useCallback, useEffect, useState } from 'react';
import { api, type AiPrefs } from '../api/client.ts';

/**
 * The shapes and the default tier this reader keeps for the assistant's chat.
 *
 * Fetched once per session and held here rather than in ChatContext: it is not
 * conversation state, nothing arrives over the socket to change it, and a chat
 * with no bot in it never needs it. The defaults below are what the composer
 * draws with while the request is in flight — the same four the server hands a
 * fresh account, so the row does not change shape underneath somebody who
 * opened the chat quickly.
 */

/** Mirrors DEFAULT_RATIOS in server/src/ai-prefs.ts. */
export const DEFAULT_RATIOS = ['1:1', '4:5', '2:3', '3:2'];

const FALLBACK: AiPrefs = { ratios: DEFAULT_RATIOS, quality: 'medium' };

export function useAiPrefs(enabled: boolean): {
  prefs: AiPrefs;
  save: (next: AiPrefs) => Promise<void>;
} {
  const [prefs, setPrefs] = useState<AiPrefs>(FALLBACK);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void api
      .aiPrefs()
      .then((next) => {
        if (!cancelled) setPrefs(next);
      })
      // Left at the defaults: a composer with four shapes in it is a working
      // composer, and a chat that refused to draw one because a preferences
      // request failed would be the wrong trade entirely.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const save = useCallback(async (next: AiPrefs) => {
    // Optimistic, then corrected: the server is what decides a shape is
    // readable, so what comes back is what is kept.
    setPrefs(next);
    setPrefs(await api.saveAiPrefs(next));
  }, []);

  return { prefs, save };
}
